import { useState, useEffect, useCallback, useRef } from 'react';

interface VersionInfo {
  version: string;
  buildTime: string;
  timestamp: number;
}

interface UseVersionCheckOptions {
  /** Polling interval in ms (default: 120000 = 2 minutes) */
  pollInterval?: number;
  /** Snooze duration in ms (default: 300000 = 5 minutes) */
  snoozeDuration?: number;
  /** Whether the check is enabled (default: true) */
  enabled?: boolean;
}

interface UseVersionCheckReturn {
  /** True when a new version has been detected */
  updateAvailable: boolean;
  /** Dismiss the notification and re-check after snooze duration */
  snooze: () => void;
  /** Reload the page to load the new version */
  reload: () => void;
}

/**
 * Polls /version.json to detect when a new deployment is available.
 * On first load, captures the current version as the baseline.
 * When the version changes, sets `updateAvailable` to true.
 *
 * Only runs in production (import.meta.env.PROD).
 */
export function useVersionCheck(options: UseVersionCheckOptions = {}): UseVersionCheckReturn {
  const { pollInterval = 120_000, snoozeDuration = 5 * 60_000, enabled = true } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineVersion = useRef<string | null>(null);
  const snoozeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduledCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSnoozed = useRef(false);

  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      // Cache-bust to always get fresh version
      const res = await fetch(`/version.json?_=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (!res.ok) return null;

      const data: VersionInfo = await res.json();
      return data;
    } catch {
      // Network error or version.json doesn't exist (dev mode) — silently ignore
      return null;
    }
  }, []);

  const checkVersion = useCallback(async () => {
    if (isSnoozed.current) return;

    const versionInfo = await fetchVersion();
    if (!versionInfo) return;

    // First check — establish baseline
    if (baselineVersion.current === null) {
      baselineVersion.current = versionInfo.version;
      return;
    }

    // Compare with baseline
    if (versionInfo.version !== baselineVersion.current) {
      setUpdateAvailable(true);
    }
  }, [fetchVersion]);

  const scheduleCheckWithJitter = useCallback(() => {
    // Spread requests across clients to avoid a synchronized burst.
    const jitterMs = Math.floor(Math.random() * 5000);

    if (scheduledCheckTimer.current) {
      clearTimeout(scheduledCheckTimer.current);
    }

    scheduledCheckTimer.current = setTimeout(() => {
      checkVersion();
    }, jitterMs);
  }, [checkVersion]);

  const snooze = useCallback(() => {
    setUpdateAvailable(false);
    isSnoozed.current = true;

    // Clear any existing snooze timer
    if (snoozeTimer.current) {
      clearTimeout(snoozeTimer.current);
    }

    // Re-enable checking after snooze duration
    snoozeTimer.current = setTimeout(() => {
      isSnoozed.current = false;
      // Immediately check again after snooze ends
      scheduleCheckWithJitter();
    }, snoozeDuration);
  }, [snoozeDuration, scheduleCheckWithJitter]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    // Only run in production
    if (!import.meta.env.PROD || !enabled) return;

    // Initial check after jitter to avoid synchronized spikes.
    scheduleCheckWithJitter();

    // Set up polling
    const interval = setInterval(scheduleCheckWithJitter, pollInterval);

    const handleWindowFocus = () => {
      scheduleCheckWithJitter();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleCheckWithJitter();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (snoozeTimer.current) {
        clearTimeout(snoozeTimer.current);
      }
      if (scheduledCheckTimer.current) {
        clearTimeout(scheduledCheckTimer.current);
      }
    };
  }, [pollInterval, enabled, scheduleCheckWithJitter]);

  return { updateAvailable, snooze, reload };
}
